import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Upload,
    Alert,
    Typography,
    Tag,
    message,
} from 'antd';
import {
    UploadOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import * as XLSX from 'xlsx';

const { Text } = Typography;

// ── types ──────────────────────────────────────────────────────────────────

export interface ParsedQuestion {
    row: number;
    question_number: string;
    prompt: string;
    answer_type: string;
    answers: Array<{ text: string; is_correct: boolean }>;
    function_id: number | null;
    function_name: string;
    task_id: number | null;
    task_name: string;
    role_id: number | null;
    role_name: string;
    difficulty: string;
    weight: number;
    warnings: string[];
}

interface RowError {
    row: number;
    question_number: string;
    question: string;
    errors: string[];
}

interface QuestionsCsvUploadModalProps {
    visible: boolean;
    onClose: () => void;
    industryId: number;
    industryName?: string;
    onQuestionsImported: (questions: ParsedQuestion[]) => void;
}

type Phase = 'upload' | 'preview' | 'success';

// ── component ──────────────────────────────────────────────────────────────

const QuestionsCsvUploadModal: React.FC<QuestionsCsvUploadModalProps> = ({
    visible,
    onClose,
    industryId,
    industryName = '',
    onQuestionsImported,
}) => {
    const [csvData, setCsvData] = useState('');
    const [fileName, setFileName] = useState('');
    const [validating, setValidating] = useState(false);
    const [preview, setPreview] = useState<ParsedQuestion[]>([]);
    const [rowErrors, setRowErrors] = useState<RowError[]>([]);
    const [phase, setPhase] = useState<Phase>('upload');
    const [importedCount, setImportedCount] = useState(0);
    const [headerErrors, setHeaderErrors] = useState<any>(null);
    const [conversionError, setConversionError] = useState<string | null>(null);

    // Reset when modal opens
    useEffect(() => {
        if (visible) resetState();
    }, [visible]);

    const resetState = () => {
        setCsvData('');
        setFileName('');
        setValidating(false);
        setPreview([]);
        setRowErrors([]);
        setPhase('upload');
        setImportedCount(0);
        setHeaderErrors(null);
        setConversionError(null);
    };

    // ── file handling ─────────────────────────────────────────────────────

    const convertExcelToDelimitedText = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const arrayBuffer = e.target?.result as ArrayBuffer;
                    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
                        reject(new Error('No worksheet found in the Excel file.'));
                        return;
                    }

                    const firstSheetName = workbook.SheetNames[0];
                    const firstSheet = workbook.Sheets[firstSheetName];
                    if (!firstSheet) {
                        reject(new Error('Unable to read the first worksheet.'));
                        return;
                    }

                    const text = XLSX.utils.sheet_to_csv(firstSheet, {
                        FS: '\t',
                        RS: '\n',
                        blankrows: false,
                    });

                    if (!text || !text.trim()) {
                        reject(new Error('The first worksheet is empty.'));
                        return;
                    }

                    resolve(text);
                } catch {
                    reject(new Error('Could not convert this Excel file. Please check the file format and try again.'));
                }
            };
            reader.onerror = () => reject(new Error('Failed to read the Excel file.'));
            reader.readAsArrayBuffer(file);
        });
    };

    const handleFileSelect = async (file: File) => {
        setHeaderErrors(null);
        setConversionError(null);
        setPreview([]);
        setRowErrors([]);
        setPhase('upload');

        const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
        const isExcel = fileExt === 'xls' || fileExt === 'xlsx';

        if (isExcel) {
            try {
                const convertedText = await convertExcelToDelimitedText(file);
                setCsvData(convertedText);
                setFileName(file.name);
                message.success('Excel file converted successfully. Click "Validate & Preview" to continue.');
            } catch (err: any) {
                setCsvData('');
                setFileName(file.name);
                setConversionError(err?.message || 'Excel conversion failed. Please use CSV or a valid Excel file.');
            }
            return false;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            setCsvData(e.target?.result as string);
            setFileName(file.name);
        };
        reader.onerror = () => {
            setCsvData('');
            setFileName(file.name);
            setConversionError('Failed to read the selected file. Please try again.');
        };
        reader.readAsText(file);
        return false; // Prevent Ant Design auto-upload
    };

    // ── validate phase ────────────────────────────────────────────────────

    const handleValidate = async () => {
        if (!csvData) return;
        setValidating(true);
        setHeaderErrors(null);
        try {
            const res = await api.post('/process-questions-csv/', {
                industry_id: industryId,
                csv_data: csvData,
                validate_only: true,
            });
            setPreview(res.data.preview || []);
            setRowErrors(res.data.row_errors || []);
            setPhase('preview');
        } catch (error: any) {
            if (error.response?.data?.header_errors) {
                setHeaderErrors(error.response.data);
            } else if (error.response?.data?.error) {
                message.error(error.response.data.error);
            } else {
                message.error('Validation failed. Please try again.');
            }
        } finally {
            setValidating(false);
        }
    };

    // ── confirm/import phase ──────────────────────────────────────────────

    const handleConfirmImport = async () => {
        if (preview.length === 0) return;
        setValidating(true);
        try {
            const res = await api.post('/process-questions-csv/', {
                industry_id: industryId,
                csv_data: csvData,
                validate_only: false,
            });
            if (res.data.success) {
                const questions: ParsedQuestion[] = res.data.questions || [];
                setImportedCount(questions.length);
                setPhase('success');
                onQuestionsImported(questions);
            } else {
                message.error(res.data.error || 'Import failed. Please try again.');
            }
        } catch {
            message.error('Import failed. Please try again.');
        } finally {
            setValidating(false);
        }
    };

    // ── sample CSV download ───────────────────────────────────────────────

    const downloadSampleCsv = () => {
        const headers = [
            'Q#', 'Function', 'Sub-skill / Task',
            'Difficulty', 'Weight',
            'Question', 'Option A', 'Option B', 'Option C', 'Option D',
            'Correct Answer', 'Answer Type',
        ];
        const rows = [
            [
                '1',
                'Invoicing',
                'Repeating invoices',
                'Bronze',
                '2',
                'What is the maximum number of repeating invoices you can set up in Xero?',
                '50',
                '100',
                '200',
                'Unlimited',
                'D',
                'Multiple Choice',
            ],
            [
                '2',
                'Payroll',
                'Superannuation settings',
                'Silver',
                '4',
                'Who is able to change the Automatic superannuation approver?',
                'The subscriber only',
                'The subscriber and current authoriser',
                'Any admin user',
                'The payroll officer',
                'B',
                'Multiple Choice',
            ],
            [
                '3',
                'Invoicing',
                'Repeating invoices',
                'Gold',
                '3',
                'Which statement about Xero repeating invoices is correct?',
                'They are sent automatically without approval',
                'They can be set to auto-approve and send',
                'They require manual approval every time',
                'They cannot include attachments',
                'B',
                'Multiple Choice',
            ],
        ];
        const content = [headers, ...rows]
            .map(r => r.map(f => `"${f}"`).join('\t'))
            .join('\n');
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'questions_sample_v2.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // ── render helpers ────────────────────────────────────────────────────

    const renderUploadPhase = () => (
        <div>
            <Text>Upload your CSV file to import questions into this assessment.</Text>

            {industryName && (
                <div style={{
                    marginTop: 8, padding: '8px 12px',
                    background: '#f0f8ff', border: '1px solid #d1ecf1', borderRadius: 4,
                }}>
                    <Text style={{ fontSize: 13, color: '#0c5460' }}>
                        <strong>Industry:</strong> {industryName}
                    </Text>
                </div>
            )}

            <div style={{ marginTop: 16 }}>
                <Upload accept=".csv,.tsv,.txt,.xls,.xlsx" beforeUpload={handleFileSelect} showUploadList={false}>
                    <Button icon={<UploadOutlined />}>Select CSV File</Button>
                </Upload>
                {fileName && (
                    <div style={{ marginTop: 8 }}>
                        <Text>
                            📄 Selected: <strong>{fileName}</strong>
                            {' '}({Math.max(0, csvData.split('\n').length - 1)} rows detected)
                        </Text>
                    </div>
                )}
            </div>

            {conversionError && (
                <Alert
                    type="error"
                    showIcon
                    message="File Conversion Error"
                    style={{ marginTop: 16 }}
                    description={
                        <div>
                            <div>{conversionError}</div>
                            <div style={{ marginTop: 8 }}>
                                Please export the spreadsheet as CSV and upload again if the issue persists.
                            </div>
                        </div>
                    }
                />
            )}

            {/* Header-level errors */}
            {headerErrors && (
                <Alert
                    type="error"
                    showIcon
                    message="CSV Format Error"
                    style={{ marginTop: 16 }}
                    description={
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                <strong>Your CSV file has incorrect column headers.</strong>
                            </div>
                            {headerErrors.missing_required?.length > 0 && (
                                <div>
                                    • Missing required columns:{' '}
                                    <strong>{headerErrors.missing_required.join(', ')}</strong>
                                </div>
                            )}
                            {Object.keys(headerErrors.suggestions || {}).length > 0 && (
                                <div style={{ marginTop: 4 }}>
                                    • Incorrect column names found:
                                    {Object.entries(headerErrors.suggestions).map(([wrong, correct]: any) => (
                                        <div key={wrong} style={{ marginLeft: 16 }}>
                                            Change "<strong>{wrong}</strong>" → "<strong>{correct}</strong>"
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div style={{
                                marginTop: 12, padding: '8px 12px',
                                background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 4,
                            }}>
                                <strong>💡 Tip:</strong> Download the sample CSV template below to use as a starting point.
                            </div>
                        </div>
                    }
                />
            )}

            {/* Reference info */}
            <div style={{ marginTop: 16, padding: 12, background: '#f0f0f0', borderRadius: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Text type="secondary" style={{ fontSize: 12, flex: 1 }}>
                        <strong>Required columns:</strong> Q#, Function, Sub-skill / Task, Difficulty, Weight, Question, Option A, Option B, Correct Answer
                    </Text>
                    <Button
                        size="small"
                        onClick={downloadSampleCsv}
                        style={{ marginLeft: 12, borderRadius: 6, fontSize: 11, height: 28 }}
                    >
                        Download Sample CSV
                    </Button>
                </div>
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    <strong>Difficulty values:</strong> Bronze, Silver, Gold &nbsp;|&nbsp;
                    <strong>Correct Answer:</strong> A, B, C, D or E (column letter)
                </Text>
                <Text type="secondary" style={{ fontSize: 12, marginTop: 2, display: 'block' }}>
                    Tip: Add up to 5 answer options in columns Option A–E. Mark the correct one with its letter in the Correct Answer column.
                </Text>
            </div>
        </div>
    );

    const renderPreviewPhase = () => {
        const warningCount = preview.filter(q => q.warnings.length > 0).length;
        return (
            <div>
                {/* Summary badges */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                    <Tag color="green" style={{ fontSize: 13, padding: '4px 10px' }}>
                        ✓ {preview.length} valid question{preview.length !== 1 ? 's' : ''}
                    </Tag>
                    {warningCount > 0 && (
                        <Tag color="orange" style={{ fontSize: 13, padding: '4px 10px' }}>
                            ⚠ {warningCount} with warnings
                        </Tag>
                    )}
                    {rowErrors.length > 0 && (
                        <Tag color="red" style={{ fontSize: 13, padding: '4px 10px' }}>
                            ✗ {rowErrors.length} with errors (will be skipped)
                        </Tag>
                    )}
                </div>

                {/* Row errors */}
                {rowErrors.length > 0 && (
                    <Alert
                        type="error"
                        showIcon
                        message={`${rowErrors.length} row(s) have errors and will be skipped`}
                        style={{ marginBottom: 12 }}
                        description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {rowErrors.map((e, i) => (
                                    <li key={i}>
                                        <strong>
                                            Row {e.row}
                                            {e.question_number ? ` (Q# ${e.question_number})` : ''}:
                                        </strong>{' '}
                                        {e.errors.join(', ')}
                                        {e.question && (
                                            <div style={{ color: '#888', fontSize: 11, fontStyle: 'italic' }}>
                                                {e.question}…
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        }
                    />
                )}

                {/* Warning summary */}
                {warningCount > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${warningCount} question(s) have warnings — review the details below before confirming`}
                        style={{ marginBottom: 12 }}
                    />
                )}

                {/* Preview list */}
                <div style={{
                    maxHeight: 380,
                    overflowY: 'auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                }}>
                    {preview.map((q, i) => (
                        <div
                            key={i}
                            style={{
                                padding: '10px 14px',
                                borderBottom: '1px solid #f0f0f0',
                                background: q.warnings.length > 0 ? '#fffbe6' : '#fafafa',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Text strong style={{ fontSize: 13 }}>
                                        {q.question_number ? `Q${q.question_number}: ` : `Row ${q.row}: `}
                                        {q.prompt.length > 110 ? q.prompt.slice(0, 110) + '…' : q.prompt}
                                    </Text>
                                    <div style={{ marginTop: 5, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        <Tag color="blue" style={{ fontSize: 11 }}>
                                            {q.answer_type || 'Unknown type'}
                                        </Tag>
                                        {q.function_name && (
                                            <Tag color="purple" style={{ fontSize: 11 }}>
                                                Fn: {q.function_name}
                                            </Tag>
                                        )}
                                        {q.task_name && (
                                            <Tag color="geekblue" style={{ fontSize: 11 }}>
                                                Task: {q.task_name}
                                            </Tag>
                                        )}
                                        {q.role_name && (
                                            <Tag color="cyan" style={{ fontSize: 11 }}>
                                                Role: {q.role_name}
                                            </Tag>
                                        )}
                                        {q.difficulty && (
                                            <Tag color="gold" style={{ fontSize: 11 }}>
                                                {q.difficulty.charAt(0).toUpperCase() + q.difficulty.slice(1)}
                                            </Tag>
                                        )}
                                        <Tag style={{ fontSize: 11 }}>Weight: {q.weight}</Tag>
                                        <Tag
                                            color={q.answers.length > 1 ? 'green' : 'default'}
                                            style={{ fontSize: 11 }}
                                        >
                                            {q.answers.length} answer{q.answers.length !== 1 ? 's' : ''}
                                        </Tag>
                                    </div>

                                    {/* Correct answer preview */}
                                    {q.answers.length > 0 && (
                                        <div style={{ marginTop: 4, fontSize: 11, color: '#555' }}>
                                            ✓ Correct:{' '}
                                            <em>
                                                {q.answers.find(a => a.is_correct)?.text?.slice(0, 70)
                                                    || <span style={{ color: '#d48806' }}>Not matched — please verify in form</span>}
                                            </em>
                                        </div>
                                    )}
                                </div>

                                {/* Status badge */}
                                <div style={{ marginLeft: 8, flexShrink: 0 }}>
                                    {q.warnings.length > 0
                                        ? <Tag color="orange" icon={<ExclamationCircleOutlined />}>Warning</Tag>
                                        : <Tag color="green" icon={<CheckCircleOutlined />}>OK</Tag>
                                    }
                                </div>
                            </div>

                            {/* Inline warnings */}
                            {q.warnings.length > 0 && (
                                <div style={{ marginTop: 6, paddingLeft: 10, borderLeft: '3px solid #faad14' }}>
                                    {q.warnings.map((w, wi) => (
                                        <div key={wi} style={{ fontSize: 11, color: '#d48806' }}>⚠ {w}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Confirm hint */}
                <div style={{
                    marginTop: 12, padding: '8px 12px',
                    background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4,
                }}>
                    <Text style={{ fontSize: 12 }}>
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                        <strong>{preview.length}</strong> question{preview.length !== 1 ? 's' : ''} will be added to the form.
                        You can review and edit each one before saving the assessment.
                    </Text>
                </div>
            </div>
        );
    };

    const renderSuccessPhase = () => (
        <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message={`${importedCount} question${importedCount !== 1 ? 's' : ''} added to the form`}
            description="Review and edit the questions in the form below, then click Save to create the assessment."
        />
    );

    // ── footer buttons ────────────────────────────────────────────────────

    const footer = () => {
        const pillStyle: React.CSSProperties = {
            borderRadius: 9999,
            fontWeight: 600,
            height: 40,
        };

        if (phase === 'upload') {
            return [
                <Button key="cancel" onClick={onClose} style={pillStyle}>
                    Cancel
                </Button>,
                <Button
                    key="validate"
                    type="primary"
                    loading={validating}
                    disabled={!csvData}
                    onClick={handleValidate}
                >
                    {validating ? 'Validating…' : 'Validate & Preview'}
                </Button>,
            ];
        }

        if (phase === 'preview') {
            return [
                <Button key="back" onClick={() => setPhase('upload')} style={pillStyle}>
                    ← Back
                </Button>,
                <Button key="cancel" onClick={onClose} style={pillStyle}>
                    Cancel
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    loading={validating}
                    disabled={preview.length === 0}
                    onClick={handleConfirmImport}
                >
                    {validating
                        ? 'Importing…'
                        : `Add ${preview.length} Question${preview.length !== 1 ? 's' : ''} to Form`}
                </Button>,
            ];
        }

        // success phase
        return [
            <Button key="close" type="primary" onClick={onClose} style={pillStyle}>
                Close & Review Questions
            </Button>,
        ];
    };

    // ── modal titles ──────────────────────────────────────────────────────

    const titleMap: Record<Phase, string> = {
        upload: 'Import Questions from CSV',
        preview: 'Review Parsed Questions',
        success: 'Questions Imported Successfully',
    };

    return (
        <Modal
            title={titleMap[phase]}
            open={visible}
            onCancel={onClose}
            footer={footer()}
            width={760}
        >
            {phase === 'upload' && renderUploadPhase()}
            {phase === 'preview' && renderPreviewPhase()}
            {phase === 'success' && renderSuccessPhase()}
        </Modal>
    );
};

export default QuestionsCsvUploadModal;
